import React from 'react'
import styled from 'styled-components'
import Link from 'next/link'
import { media } from 'styled-bootstrap-grid'
import StaticImage from '@components/images/StaticImg'
import { ASSET_PATH, COMPANY, legalLinks, ROUTES } from '../content'
import { hairline, ink, muted } from '../styled'
import SocialIcons from './SocialIcons'

const Wrap = styled.footer`
  border-top: 1px solid ${hairline};
  max-width: 1120px;
  margin: 0 auto;
  padding: 36px 16px 44px;
  font-size: 13.5px;
  color: ${muted};

  ${media.sm`padding: 40px 24px 48px;`}
`

const Programmes = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px 26px;
  padding-bottom: 26px;
  margin-bottom: 30px;
  border-bottom: 1px solid ${hairline};

  ${media.md`justify-content: flex-start;`}
`

const ProgrammeLink = styled.a`
  && {
    color: ${ink};
    font-size: 14.5px;
    font-weight: 600;
  }

  &&:hover {
    color: ${muted};
  }
`

const Base = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 26px;
  text-align: center;

  ${media.md`
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    text-align: right;
    gap: 40px;
  `}
`

const Brand = styled.a`
  display: block;

  img {
    height: 44px;
    width: auto;

    ${media.md`height: 40px;`}
  }
`

const Right = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;

  ${media.md`
    align-items: flex-end;
    gap: 14px;
  `}
`

const LegalRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;

  ${media.md`
    flex-direction: row;
    align-items: center;
    gap: 24px;
  `}
`

const LegalLinks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  ${media.md`
    flex-direction: row;
    gap: 22px;
  `}
`

// The socials sit under the logo on a phone and after the legal links on desktop,
// so the order flips at the breakpoint rather than the markup changing.
const Socials = styled.div`
  order: -1;

  ${media.md`order: 0;`}
`

const LegalLink = styled.a`
  && {
    color: ${muted};
    font-size: 14px;
  }

  &&:hover {
    color: ${ink};
  }
`

const Small = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: ${muted};
  max-width: 420px;

  ${media.md`max-width: none;`}
`

const programmeLinks = [
  { href: ROUTES.accelerate, label: 'Accelerate' },
  { href: ROUTES.fullCertification, label: 'Full Certification' },
  { href: ROUTES.aboutMartin, label: 'About Martin' },
  { href: ROUTES.letter, label: 'Letter from the founder' },
]

/**
 * @param legalOnly  drops the programme links and socials. The paid-social page
 *                   is a closed lander — nothing should lead away from purchase —
 *                   but ad policy still requires the legal links.
 */
const ProgramFooter = ({ legalOnly = false }) => (
  <Wrap>
    {!legalOnly && (
      <Programmes>
        {programmeLinks.map((link) => (
          <Link key={link.href} href={link.href} passHref>
            <ProgrammeLink>{link.label}</ProgrammeLink>
          </Link>
        ))}
      </Programmes>
    )}

    <Base>
      <Link href={ROUTES.jumpstart} passHref>
        <Brand aria-label="Entrepreneur Seminar">
          <StaticImage src={`${ASSET_PATH}/es-logo.png`} alt="Entrepreneur Seminar" />
        </Brand>
      </Link>

      <Right>
        <LegalRow>
          <LegalLinks>
            {legalLinks.map((link) => (
              <Link key={link.href} href={link.href} passHref>
                <LegalLink>{link.label}</LegalLink>
              </Link>
            ))}
          </LegalLinks>
          {!legalOnly && (
            <Socials>
              <SocialIcons />
            </Socials>
          )}
        </LegalRow>

        <Small>
          {COMPANY.tradingLine}
          <br />
          {COMPANY.copyright} {COMPANY.companyNumber}
        </Small>
      </Right>
    </Base>
  </Wrap>
)

export default ProgramFooter
